import {useCallback, useEffect, useRef, useState} from 'react';

/**
 * Высота элемента в CSS-пикселях — по ResizeObserver, пока элемент на экране.
 * Ref — колбэк, а не useRef: элемент могут смонтировать и снять когда угодно
 * (другая ветка рендера), и наблюдение переезжает вместе с ним. Снятый
 * элемент и браузер без ResizeObserver (тесты, старые браузеры) — null:
 * верстка тогда обходится без замера. Новый элемент заново начинает с null
 *
 * @returns {[function(HTMLElement|null): void, number|null]} ref-колбэк и высота
 */
export const useObservedHeight = () => {
  const [height, setHeight] = useState(null);
  const observerRef = useRef(null);

  const ref = useCallback((node) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    setHeight(null);
    if (!node || typeof ResizeObserver === 'undefined') {
      return;
    }
    const measure = () => {
      const nextHeight = node.getBoundingClientRect().height;
      setHeight((prevHeight) => (prevHeight === nextHeight ? prevHeight : nextHeight));
    };
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    observerRef.current = observer;
  }, []);

  useEffect(() => () => observerRef.current?.disconnect(), []);

  return [ref, height];
};
